import {
  Box,
  Text,
  Flex,
  MantineProvider,
  ActionIcon,
  Group,
  Button,
  NumberFormatter,
  Loader,
  Select,
  Image,
} from "@mantine/core";
import { DataGrid } from "mantine-data-grid";
import { If, Then } from "react-if";
import { AmbassadorsGridView } from "./AmbassadorsGridView";
import { AmbassadorHeader } from "./AmbassadorHeader";
import { useNavigate } from "react-router";
import { actions, useTrackedStore } from "../../../../store/useStore";
import { useGetAllAmbassadors } from "../../../../api/ambassador/useAmbassador";
import { tableTheme, theme } from "../../Theme";
import { IconNextIcon, IconPrevIcon } from "../../static/icons/Icons";
import AmbassadorFilters from "./AmbassadorFilters";
import withClientNumber from "../../hoc/ClientNumber";

function ManageAmbassador({ clientNumber }: { clientNumber?: string }) {
  const {
    filter: { data: filterData },
    ambassadors: { page, pageSize, mode, search },
  } = useTrackedStore();

  const _page = page();
  const _pageSize = pageSize();
  const _mode = mode();
  const filters = filterData();
  const _search = search();

  const { data: ambassadors, isFetching: isFetchingList } =
    useGetAllAmbassadors({
      per_page: _pageSize,
      page: _page,
      params: {
        sort: "desc",
        sort_by: "id",
        search: _search,
        ...filters,
        country_id: filters?.country_id,
        state_id: filters?.state_id,
        city_id: filters?.city_id,
      },
    });

  const authenticateUser = (url: string) => {
    if (localStorage.getItem("ymXo8h") !== null) {
      const state = localStorage.getItem("ymXo8h");
      if (state !== null) {
        const parsedJSON = JSON.parse(state);
        const _identifier = parsedJSON?.state?.user?.identifier;
        const anchor = document.createElement("a");
        anchor.href = `${url}/t/${_identifier}?referral=user`;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        anchor.click();
      }
    } else {
      const anchor = document.createElement("a");
      anchor.href = `${url}/signin`;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      anchor.click();
    }
  };

  return (
    <MantineProvider theme={theme}>
      <Box px={38} py={27}>
        <AmbassadorHeader details={ambassadors} />
        <AmbassadorFilters key={JSON.stringify(ambassadors)} />
        <Flex gap="1rem" mb={20} justify="end" align="center">
          <Text fz={14}>POWERED BY</Text>
          <Group align="end">
            <Image h={30} src="assets/logos/firmli-logo.png" />
          </Group>
        </Flex>
        <If condition={_mode === "grid"}>
          <Then>
            <AmbassadorsGridView />
          </Then>
        </If>

        <If condition={_mode === "list"}>
          <Then>
            <DataGrid
              withColumnResizing
              withColumnFilters
              withSorting
              state={{
                pagination: {
                  pageIndex: _page - 1,
                  pageSize: _pageSize,
                },
              }}
              loading={isFetchingList}
              data={ambassadors?.recordsInfo ?? []}
              empty={
                <Flex justify="center">
                  <Text>No data found</Text>
                </Flex>
              }
              styles={{ ...tableTheme }}
              columns={[
                {
                  header: "#",
                  enableSorting: true,
                  enableColumnFilter: true,
                  cell: ({ row }) => (_page - 1) * _pageSize + row.index + 1,
                },
                {
                  accessorFn: (row) =>
                    `${(row as any).userInfo?.first_name ?? ""} ${
                      (row as any).userInfo?.last_name ?? ""
                    }`,

                  id: "fullName",
                  header: "Name",
                  enableColumnFilter: true,
                  enableSorting: true,
                },
                {
                  accessorKey: "ambassadorTypeInfo.name",
                  header: "Ambassador Type",
                },
                {
                  accessorKey: "institution_data.name",
                  header: "University",
                },
                {
                  accessorKey: "country_of_origin.name",
                  header: "Country",
                },
                {
                  header: "Action",
                  cell: ({ row }: { row: any }) => (
                    <Button
                      variant="outline"
                      size="xs"
                      onClick={() =>
                        authenticateUser(row?.original?.client_frontend_url)
                      }
                    >
                      Chat
                    </Button>
                  ),
                },
              ]}
            />
          </Then>
        </If>
        <Flex
          mb={10}
          mt={"md"}
          align={"center"}
          justify="space-between"
          wrap="wrap"
          gap="md"
        >
          <Flex align={"center"} gap="md" className="number_input" wrap="wrap">
            <Select
              id="change-perPage-button"
              w={80}
              data={["15", "25", "50", "100"]}
              value={String(_pageSize)}
              onChange={(value: any) =>
                actions.ambassadors.setPageSize(value as 25 | 50 | 100)
              }
              searchable={false}
            />
            <Text c="#5F6368" fz={14}>
              Showing ambassadors{" "}
              {isFetchingList ? (
                <Loader my="auto" mx={4} size={12} />
              ) : (
                <NumberFormatter
                  thousandSeparator
                  value={
                    ambassadors?.metaInfo?.totalRecords == 0
                      ? 0
                      : ambassadors?.metaInfo?.perPage *
                          (ambassadors?.metaInfo?.page - 1) +
                        1
                  }
                />
              )}
              {" to "}
              {isFetchingList ? (
                <Loader my="auto" mx={4} size={12} />
              ) : (
                <NumberFormatter
                  thousandSeparator
                  value={ambassadors?.metaInfo?.pageEndOffset}
                />
              )}{" "}
              of{" "}
              <NumberFormatter
                thousandSeparator
                value={ambassadors?.metaInfo?.totalRecords}
              />
            </Text>
          </Flex>
          <Flex justify="center" align="center">
            <Group>
              <ActionIcon
                id="previous-page-button"
                size="xs"
                variant="transparent"
                disabled={!ambassadors?.metaInfo?.hasPreviousPage}
                onClick={() => actions.ambassadors.setPrevPage()}
              >
                <IconPrevIcon
                  color={
                    ambassadors?.metaInfo?.hasPreviousPage
                      ? "#1A73E8"
                      : "#5F6368"
                  }
                  opacity={ambassadors?.metaInfo?.hasPreviousPage ? 1 : 0.5}
                />
              </ActionIcon>
              <Text fz={14}>
                Page {_page} of{" "}
                <span style={{ color: "#5F6368" }}>
                  {ambassadors?.metaInfo?.lastPage}
                </span>
              </Text>
              <ActionIcon
                id="next-page-button"
                size="xs"
                variant="transparent"
                disabled={!ambassadors?.metaInfo?.hasNextPage}
                onClick={() => actions.ambassadors.setNextPage()}
              >
                <IconNextIcon
                  color={
                    ambassadors?.metaInfo?.hasNextPage ? "#1A73E8" : "#5F6368"
                  }
                />
              </ActionIcon>
            </Group>
          </Flex>
          <Flex></Flex>
        </Flex>
      </Box>
    </MantineProvider>
  );
}

export default withClientNumber(ManageAmbassador);
